import { useEffect } from "react";
import {
  CircularProgress,
  Pagination,
  PaginationItemType,
  Select,
  SelectItem,
  cn,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { BiSolidUpArrow } from "react-icons/bi";

import GenericTable from "./Kit/GenericTable";
import MAddToListsMenu from "./MAddToListsMenu";

import { ChevronIcon } from "../assets/icons/audio-icons";
import { resetSelectedCalls } from "../redux/actions";

const PaginationTable = ({
  columns = [],
  items = [],
  emptyState = "No records to display",
  isLoading = false,
  renderCell,
  onRowAction = null,
  sortDescriptor,
  setSortDescriptor,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  selectAllAction = () => {},
  customFiltersRender = undefined,
  isSelectAllChecked = false,
  displaySelectAll = true,
  overrideTotalItems = null,
  itemNaming = "calls",
  isClickable = true,
  overrideNbPageOptions = null,
}) => {
  const { t } = useTranslation();
  const { selectedCalls = [] } = useSelector((state) => state.calls.calls);
  const { algoliaTotalItems, algoliaTotalPages } = useSelector((state) => state.calls.calls);
  const { scopes, loading: algoliaLoading } = useSelector((state) => state.algolia.algolia);

  const teamsScope = scopes?.teams || [];
  const departmentsScope = scopes?.departments || [];
  const localisationsScope = scopes?.localisations || [];
  const hasGlobalVisibility =
    teamsScope.includes("all") ||
    departmentsScope.includes("all") ||
    localisationsScope.includes("all");

  const shouldOverrideAlgoliaPagination = overrideTotalItems !== null;
  const totalItems = shouldOverrideAlgoliaPagination ? overrideTotalItems : algoliaTotalItems;
  const totalPages = shouldOverrideAlgoliaPagination
    ? Math.ceil(overrideTotalItems / rowsPerPage)
    : algoliaTotalPages;

  const dispatch = useDispatch();
  const onResetSelection = () => {
    dispatch(resetSelectedCalls());
  };

  useEffect(() => {
    setPage(1);
    onResetSelection();
  }, [totalItems]);

  const renderTopContent = () => {
    return (
      <div className="flex items-center justify-between w-full py-2">
        {selectedCalls.length > 0 && (
          <div className="flex items-center">
            {/* <div
                className="cursor-pointer text-sm text-blue-700 mr-[12px] whitespace-nowrap"
                onClick={onResetSelection}
                onKeyDown={onResetSelection}
              >
                {`Unselect All (${selectedCalls.length})`}
              </div> */}
            {hasGlobalVisibility && <MAddToListsMenu />}
          </div>
        )}
        {customFiltersRender !== undefined && customFiltersRender !== null && customFiltersRender}
      </div>
    );
  };

  const renderPaginationItem = ({
    ref,
    key,
    value,
    isActive,
    onNext,
    onPrevious,
    setPage,
    className,
  }) => {
    if (value === PaginationItemType.NEXT) {
      return (
        <button
          type="button"
          key={key}
          className={cn(
            className,
            "items-center space-x-1 bg-transparent min-w-20 w-auto h-8 ml-3",
            page >= totalPages && "text-gray-200"
          )}
          onClick={onNext}
          disabled={page >= totalPages}
        >
          {t("Next")}{" "}
          <ChevronIcon
            className="w-8 rotate-180"
            size={18}
          />
        </button>
      );
    }
    if (value === PaginationItemType.PREV) {
      return (
        <button
          type="button"
          key={key}
          className={cn(
            className,
            "flex items-center space-x-1 bg-transparent min-w-20 w-auto h-8 mr-3",
            page <= 1 && "text-gray-200"
          )}
          onClick={onPrevious}
          disabled={page <= 1}
        >
          <ChevronIcon
            className="w-8"
            size={18}
          />{" "}
          {t("Prev")}
        </button>
      );
    }
    if (value === PaginationItemType.DOTS) {
      return (
        <button
          type="button"
          key={key}
          className={className}
        >
          ...
        </button>
      );
    }
    // cursor is the default item
    return (
      <button
        type="button"
        key={key}
        ref={ref}
        className={cn(className)}
        onClick={() => {
          setPage(value);
        }}
      >
        {value}
      </button>
    );
  };

  const renderBottomContent = () => {
    const nbPageOptions = overrideNbPageOptions != null ? overrideNbPageOptions : [12, 25, 50, 100];

    return (
      <div className="flex items-center justify-between items-center pt-4 px-2">
        <div className="flex-shrink-0">
          <div className="flex items-center text-default-700 gap-[4px]">
            <div className="text-xs">{t("Showing")}</div>
            <Select
              aria-label="Select items per page"
              placeholder="Select items per page"
              selectedKeys={[rowsPerPage?.toString()]}
              defaultSelectedKeys={["12"]}
              className="w-[78px] p-0 shrink-0"
              color="primary"
              classNames={{
                trigger: "bg-white shadow-none",
                selectorIcon: "w-2.5",
                label: "hidden",
                innerWrapper: "p-0",
                mainWrapper: "p-0 h-8",
                value: "text-sm leading-none text-primary font-medium font-PlusJakartaSans",
              }}
              size="sm"
              onChange={(e) => {
                const rows_per_page = e.target.value;
                if (rows_per_page === "") return;
                setRowsPerPage(rows_per_page);
                setPage(1);
                // handleOnPageChanged(1, rows_per_page);
              }}
              selectorIcon={<BiSolidUpArrow />}
            >
              {nbPageOptions.map((nbPageOption) => (
                <SelectItem
                  key={`${nbPageOption}`}
                  value={nbPageOption}
                >
                  {`${nbPageOption}`}
                </SelectItem>
              ))}
            </Select>
            <div className="text-default-700 text-xs whitespace-nowrap">{` ${t(
              `${itemNaming} out of`
            )} ${totalItems}`}</div>
          </div>
        </div>
        {!isLoading && (
          <Pagination
            showControls
            classNames={{
              cursor: "rounded-none",
              wrapper:
                "bg-white rounded-md border-1 border-[#E5E5E5] border-solid gap-0 shadow-drop-100",
              item: "rounded-none",
            }}
            color="primary"
            page={page}
            total={totalPages}
            variant="light"
            onChange={(new_page) => {
              setPage(new_page);
            }}
            renderItem={renderPaginationItem}
          />
        )}
      </div>
    );
  };

  const hasItems = isLoading || items?.length > 0;

  return (
    <>
      {renderTopContent()}
      {hasItems && (
        <GenericTable
          selectAllAction={selectAllAction}
          displaySelectAll={displaySelectAll}
          isSelectAllChecked={isSelectAllChecked}
          items={items}
          getItemContent={renderCell}
          onRowClick={onRowAction}
          titles={columns}
          isClickable={isClickable}
          sortDescriptor={sortDescriptor}
          setSortDescriptor={setSortDescriptor}
        />
      )}
      {isLoading && (
        <div className="w-full min-h-full flex items-center justify-center">
          <CircularProgress
            aria-label="Loading..."
            color="primary"
            label={t("Loading...")}
          />
        </div>
      )}
      {!hasItems && emptyState}
      {renderBottomContent()}
    </>
  );
};

export default PaginationTable;
